import React, { useState, useEffect, lazy, Suspense } from 'react';
import axios from 'axios';
import '@/App.css';
import '@/utils/axiosSetup';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import '@/i18n/config';
import { ThemeProvider } from '@/components/ThemeProvider';
import { HelmetProvider } from 'react-helmet-async';
import { API } from '@/utils/apiConfig';

// Critical path - eagerly loaded
import SiteRouter from '@/pages/SiteRouter';

// Lazy load everything else
const AdminLogin = lazy(() => import('@/pages/AdminLogin'));
const AdminLayout = lazy(() => import('@/pages/AdminLayout'));
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'));
const Services = lazy(() => import('@/pages/admin/Services'));
const Projects = lazy(() => import('@/pages/admin/Projects'));
const FAQ = lazy(() => import('@/pages/admin/FAQ'));
const Messages = lazy(() => import('@/pages/admin/Messages'));
const Stats = lazy(() => import('@/pages/admin/Stats'));
const Partners = lazy(() => import('@/pages/admin/Partners'));
const Settings = lazy(() => import('@/pages/admin/Settings'));
const TopBarSettings = lazy(() => import('@/pages/admin/TopBarSettings'));
const ThemeSettings = lazy(() => import('@/pages/admin/ThemeSettings'));
const CompanySettings = lazy(() => import('@/pages/admin/CompanySettings'));
const SiteSettingsPage = lazy(() => import('@/pages/admin/SiteSettings'));
const CategoriesPage = lazy(() => import('@/pages/admin/Categories'));
const TeamPage = lazy(() => import('@/pages/admin/Team'));
const SecuritySettings = lazy(() => import('@/pages/admin/SecuritySettings'));
const PageSections = lazy(() => import('@/pages/admin/PageSections'));
const SuperAdminDashboard = lazy(() => import('@/pages/admin/SuperAdminDashboard'));
const AITemplateGenerator = lazy(() => import('@/pages/admin/AITemplateGenerator'));
const TemplateCodeEditor = lazy(() => import('@/pages/admin/TemplateCodeEditor'));
const AIContentAssistant = lazy(() => import('@/pages/admin/AIContentAssistant'));
const AIWebsiteBuilder = lazy(() => import('@/pages/admin/AIWebsiteBuilder'));
const AIPhotoFinder = lazy(() => import('@/pages/admin/AIPhotoFinder'));
const ABTestingPanel = lazy(() => import('@/pages/admin/ABTestingPanel'));
const EnergomatEditor = lazy(() => import('@/pages/admin/EnergomatEditor'));
const EnergomatReadings = lazy(() => import('@/pages/admin/EnergomatReadings'));
const ChartSimulation = lazy(() => import('@/pages/admin/ChartSimulation'));
const StatsSettings = lazy(() => import('@/pages/admin/StatsSettings'));
const ContactFormSettings = lazy(() => import('@/pages/admin/ContactFormSettings'));
const ExternalModules = lazy(() => import('@/pages/admin/ExternalModules'));
const BlogAdmin = lazy(() => import('@/pages/admin/BlogAdmin'));
const BlogSettings = lazy(() => import('@/pages/admin/BlogSettings'));
const CalcSettings = lazy(() => import('@/pages/admin/CalcSettings'));
const BookingsAdmin = lazy(() => import('@/pages/admin/BookingsAdmin'));
const WidgetPositions = lazy(() => import('@/pages/admin/WidgetPositions'));
const EshopAdmin = lazy(() => import('@/pages/admin/EshopAdmin'));
const RestaurantMenuEditor = lazy(() => import('@/pages/admin/RestaurantMenuEditor'));
const FitnessPricingEditor = lazy(() => import('@/pages/admin/FitnessPricingEditor'));
const ChatbotSettings = lazy(() => import('@/pages/admin/ChatbotSettings'));
const BookingPage = lazy(() => import('@/pages/BookingPage'));
const ABTestingAdmin = lazy(() => import('@/pages/admin/ABTestingAdmin'));
const EditorPermissions = lazy(() => import('@/pages/admin/EditorPermissions'));
const PortfolioEditor = lazy(() => import('@/pages/admin/PortfolioEditor'));
const AcceptInvite = lazy(() => import('@/pages/AcceptInvite'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const DiagnosticsPage = lazy(() => import('@/pages/DiagnosticsPage'));
const BlogPages = lazy(() => import('@/pages/BlogPages'));
const ResetPasswordPages = lazy(() => import('@/pages/ResetPassword'));

// Widgets - lazy loaded, not needed for first paint
const DiagPanel = lazy(() => import('@/components/DiagPanel'));
const FloatingCTA = lazy(() => import('@/components/FloatingCTA'));
const ChatWidget = lazy(() => import('@/components/ChatWidget'));
const CalcWidget = lazy(() => import('@/components/CalcWidget'));
const BookingWidget = lazy(() => import('@/components/BookingWidget'));
const AIChatWidget = lazy(() => import('@/components/AIChatWidget'));

// Lazy wrappers for named exports
const BlogList = (props) => {
  const [Comp, setComp] = React.useState(null);
  React.useEffect(() => { import('@/pages/BlogPages').then(m => setComp(() => m.BlogList)); }, []);
  return Comp ? <Comp {...props} /> : null;
};
const BlogPost = (props) => {
  const [Comp, setComp] = React.useState(null);
  React.useEffect(() => { import('@/pages/BlogPages').then(m => setComp(() => m.BlogPost)); }, []);
  return Comp ? <Comp {...props} /> : null;
};
const ForgotPassword = (props) => {
  const [Comp, setComp] = React.useState(null);
  React.useEffect(() => { import('@/pages/ResetPassword').then(m => setComp(() => m.ForgotPassword)); }, []);
  return Comp ? <Comp {...props} /> : null;
};
const ResetPassword = (props) => {
  const [Comp, setComp] = React.useState(null);
  React.useEffect(() => { import('@/pages/ResetPassword').then(m => setComp(() => m.ResetPassword)); }, []);
  return Comp ? <Comp {...props} /> : null;
};

// Protected route wrapper
const ProtectedRoute = ({ token, children }) => {
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

// Minimal loading fallback
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#16a34a', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
  </div>
);

function getInitialToken() {
  const params = new URLSearchParams(window.location.search);
  const autoToken = params.get('auto_token');
  if (autoToken) {
    localStorage.setItem('token', autoToken);
    params.delete('auto_token');
    const cleanPath = window.location.pathname + (params.toString() ? `?${params}` : '');
    window.history.replaceState({}, '', cleanPath);
    return autoToken;
  }
  return localStorage.getItem('token') || null;
}

function App() {
  const [token, setToken] = useState(getInitialToken);
  const [userRole, setUserRole] = useState(localStorage.getItem('user_role') || null);

  useEffect(() => {
    if (token && !userRole) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          const role = r.data?.role || 'admin';
          localStorage.setItem('user_role', role);
          setUserRole(role);
        })
        .catch(() => {});
    }
  }, [token, userRole]);

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user_role');
      setUserRole(null);
    }
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => {
      const l = document.getElementById('initial-loader');
      if (l) { l.style.opacity = '0'; l.style.transition = 'opacity 0.3s'; setTimeout(() => l.remove(), 300); }
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const handleSetToken = (newToken, role) => {
    setToken(newToken);
    if (role) {
      setUserRole(role);
      localStorage.setItem('user_role', role);
    }
  };

  const getDefaultAdminRoute = () => {
    return userRole === 'superadmin' ? '/admin/superadmin' : '/admin/dashboard';
  };

  return (
    <HelmetProvider>
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<SiteRouter />} />
            <Route path="/cs" element={<SiteRouter />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/diagnostika" element={<DiagnosticsPage />} />
            <Route path="/admin" element={token ? <Navigate to={getDefaultAdminRoute()} replace /> : <Navigate to="/admin/login" replace />} />
            <Route path="/admin/login" element={token ? <Navigate to={getDefaultAdminRoute()} replace /> : <AdminLogin setToken={handleSetToken} />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route path="/admin/accept-invite" element={<AcceptInvite setToken={handleSetToken} />} />
            <Route path="/admin/*" element={
              <ProtectedRoute token={token}>
                <AdminLayout token={token} setToken={setToken} userRole={userRole} setUserRole={setUserRole} />
              </ProtectedRoute>
            }>
              <Route path="dashboard" element={<Dashboard token={token} />} />
              <Route path="superadmin" element={<SuperAdminDashboard token={token} />} />
              <Route path="ai-templates" element={<AITemplateGenerator token={token} />} />
              <Route path="template-editor" element={<TemplateCodeEditor token={token} />} />
              <Route path="ai-content" element={<AIContentAssistant token={token} />} />
              <Route path="ai-builder" element={<AIWebsiteBuilder token={token} />} />
              <Route path="ai-photos" element={<AIPhotoFinder token={token} />} />
              <Route path="ab-testing" element={<ABTestingPanel token={token} />} />
              <Route path="services" element={<Services token={token} />} />
              <Route path="projects" element={<Projects token={token} />} />
              <Route path="faq" element={<FAQ token={token} />} />
              <Route path="messages" element={<Messages token={token} />} />
              <Route path="stats" element={<Stats token={token} />} />
              <Route path="partners" element={<Partners token={token} />} />
              <Route path="company" element={<CompanySettings token={token} />} />
              <Route path="settings" element={<Settings token={token} />} />
              <Route path="topbar" element={<TopBarSettings token={token} />} />
              <Route path="theme" element={<ThemeSettings token={token} />} />
              <Route path="site-settings" element={<SiteSettingsPage token={token} />} />
              <Route path="categories" element={<CategoriesPage token={token} />} />
              <Route path="team" element={<TeamPage token={token} />} />
              <Route path="security" element={<SecuritySettings token={token} />} />
              <Route path="page-sections" element={<PageSections token={token} />} />
              <Route path="energomat" element={<EnergomatEditor token={token} />} />
              <Route path="energomat-readings" element={<EnergomatReadings token={token} />} />
              <Route path="chart-simulation" element={<ChartSimulation token={token} />} />
              <Route path="stats-settings" element={<StatsSettings token={token} />} />
              <Route path="contact-form-settings" element={<ContactFormSettings token={token} />} />
              <Route path="external-modules" element={<ExternalModules token={token} />} />
              <Route path="blog" element={<BlogAdmin token={token} />} />
              <Route path="blog-settings" element={<BlogSettings token={token} />} />
              <Route path="calculator-settings" element={<CalcSettings token={token} />} />
              <Route path="bookings" element={<BookingsAdmin token={token} />} />
              <Route path="widget-positions" element={<WidgetPositions token={token} />} />
              <Route path="shop" element={<EshopAdmin token={token} />} />
              <Route path="content-editor" element={<PageSections token={token} />} />
              <Route path="restaurant-menu" element={<RestaurantMenuEditor token={token} />} />
              <Route path="fitness-editor" element={<FitnessPricingEditor token={token} />} />
              <Route path="chatbot" element={<ChatbotSettings token={token} />} />
              <Route path="ab-testing" element={<ABTestingAdmin token={token} />} />
              <Route path="portfolio" element={<PortfolioEditor token={token} />} />
              <Route path="editor-permissions" element={<EditorPermissions token={token} />} />
              <Route index element={<Navigate to={getDefaultAdminRoute()} replace />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" />
        <Suspense fallback={null}>
          <FloatingCTA />
          <ChatWidget />
          <CalcWidget />
          <BookingWidget />
          <AIChatWidget />
          <DiagPanel />
        </Suspense>
      </div>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
